import { Message } from "@/i18n/Message";
import { lazy, Suspense, useEffect, useRef, useSyncExternalStore } from "react";

import {
  cancelAuthRecovery,
  completeAuthRecovery,
  dismissAuthRecovery,
  getAuthRecoveryView,
  subscribeAuthRecovery,
} from "../auth/authRecovery";
// Loads when recovery opens; most visits never need it.
const AccountSignIn = lazy(() => import("./AccountSignIn"));

const FOCUSABLE =
  'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]';

/**
 * The shell's session-expired challenge: the regular first-party sign-in
 * (passkey first, email code when a passkey is unavailable) in a modal over
 * whatever the person was using. The screen underneath — including a framed
 * App — stays mounted, so nothing about its state is lost.
 */
export default function AuthRecoveryDialog() {
  const view = useSyncExternalStore(
    subscribeAuthRecovery,
    getAuthRecoveryView,
    getAuthRecoveryView,
  );
  const dialog = useRef<HTMLDivElement>(null);
  const open = view.kind !== "idle";
  // Keep the Escape handler stable while the view object changes per attempt.
  const cancel = useRef(cancelAuthRecovery);
  cancel.current = cancelAuthRecovery;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // The dialog container is not a useful focus target. The sign-in card is a
    // lazy chunk, so keep looking until its first real control exists.
    let focusTimer: number | undefined;
    let focusDeadline: number | undefined;
    const focusFirstControl = () => {
      const first = dialog.current?.querySelector<HTMLElement>(FOCUSABLE);
      if (!first) return;
      first.focus();
      if (focusTimer !== undefined) window.clearInterval(focusTimer);
      if (focusDeadline !== undefined) window.clearTimeout(focusDeadline);
    };
    focusTimer = window.setInterval(focusFirstControl, 50);
    focusDeadline = window.setTimeout(() => {
      if (focusTimer !== undefined) window.clearInterval(focusTimer);
    }, 2000);
    focusFirstControl();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        cancel.current();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const controls = Array.from(
        dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((node) => node.getClientRects().length > 0);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      if (focusTimer !== undefined) window.clearInterval(focusTimer);
      if (focusDeadline !== undefined) window.clearTimeout(focusDeadline);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="auth-recovery-backdrop">
      <div
        ref={dialog}
        className="auth-recovery-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-recovery-status"
        tabIndex={-1}
      >
        <Suspense fallback={null}>
          <AccountSignIn
            key={view.attempt}
            notice={
              <p id="auth-recovery-status">
                <Message message={"Your session expired. Sign in to continue where you left off."} />
              </p>
            }
            onBack={cancelAuthRecovery}
            onSessionEstablished={completeAuthRecovery}
            onAccountSignedIn={dismissAuthRecovery}
          />
        </Suspense>
      </div>
    </div>
  );
}
